import React from 'react';

import { Alert, Box, Stack, Typography, useTheme } from '@mui/material';

import { OfferShare } from './departureInventory';

/** The colour a share is marked in: the theme's primary for the tour itself, then one per LTLE. */
export function useShareColour(): (share: OfferShare) => string {
  const theme = useTheme();
  const ltleColours = [
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
  ];

  return (share) =>
    share.colourIndex === undefined ? theme.palette.primary.main : ltleColours[share.colourIndex % ltleColours.length];
}

/** True when anything on the departure was sold by an LTLE, so a split is worth showing. */
export function hasLinkedShare(shares: Array<OfferShare>): boolean {
  return shares.some((share) => !share.isOwn && share.units > 0);
}

/** A selling tour's colour and name, with its travellers when given. */
export function ShareLabel({ share, units }: { share: OfferShare; units?: number }) {
  const colourOf = useShareColour();

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
      <Box width={8} height={8} borderRadius="50%" bgcolor={colourOf(share)} flexShrink={0} />
      <Typography variant="caption" noWrap>
        {share.label}
      </Typography>
      {units !== undefined && (
        <Typography variant="caption" color="text.secondary" flexShrink={0}>
          · {units}
        </Typography>
      )}
    </Stack>
  );
}

/** Each selling tour on its own line, with its travellers. */
export function ShareList({ shares }: { shares: Array<OfferShare> }) {
  return (
    <Stack spacing={0.25}>
      {shares
        .filter((share) => share.units !== 0)
        .map((share) => (
          <ShareLabel key={share.tourOfferId ?? 'own'} share={share} units={share.units} />
        ))}
    </Stack>
  );
}

/**
 * In place of an LTLE's inventory: the departure is shared with its base tour, which shows
 * everyone on it with the LTLE's bookings marked, so the LTLE does not show a partial count.
 */
export function SharedInventoryNote() {
  return (
    <Alert severity="info">
      Inventory for this departure is shared with the tour this LTLE is built on, which shows everyone on it with this
      LTLE&apos;s bookings marked.
    </Alert>
  );
}
